import { Modal } from '../../ui/Modal.tsx';

interface PhotoSourceSheetProps {
  /** Opens the rear camera. Hidden by CSS where there is no camera to reach. */
  onTakePhoto: () => void;
  onChoosePhoto: () => void;
  onClose: () => void;
}

/**
 * Where a photo comes from, asked as a sheet rather than as two buttons in the editor.
 *
 * The editor is a row that has opened, and two filled buttons side by side in it were the
 * loudest thing on a card whose other controls are a name and a note. One quiet "Add
 * photo" says the same thing in a third of the width, and the choice between camera and
 * gallery — which is a choice, and one people make deliberately — gets a whole sheet in
 * thumb reach instead of two 40px targets at the top of the screen.
 *
 * It is the first thing on the shopping path to use {@link Modal}, and it earns it the way
 * the photo viewer does: nothing is interrupted, because the sheet *is* the tap's answer.
 *
 * The rows are `.menu__item`s, the same ones `AppMenu` uses, so this costs no CSS of its
 * own. Cancel is a row rather than a button for the same reason it is there — a sheet with
 * no visible way out teaches people to guess at the backdrop.
 */
export function PhotoSourceSheet({ onTakePhoto, onChoosePhoto, onClose }: PhotoSourceSheetProps) {
  return (
    <Modal title="Add photo" variant="sheet" onClose={onClose}>
      <div className="menu">
        {/* Present in the markup on every device and hidden by CSS on a desktop, where
            `capture` is ignored and this row would open the same dialog as the one below
            it — see the note on the class. */}
        <button
          type="button"
          className="menu__item menu__item--icon menu__item--camera"
          onClick={onTakePhoto}
        >
          <span className="menu__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
              <path
                d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.6l1.2-2h7.4l1.2 2h2.6A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-9z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="13" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.7" />
            </svg>
          </span>
          <span className="menu__label">Take photo</span>
        </button>

        <button
          type="button"
          className="menu__item menu__item--icon"
          onClick={onChoosePhoto}
        >
          <span className="menu__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
              <rect
                x="3.5"
                y="5"
                width="17"
                height="14"
                rx="2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
              />
              <path
                d="M4 16l4.5-4.5 3.5 3.5 2.5-2.5L20 17"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="menu__label">Choose photo</span>
        </button>

        <button
          type="button"
          className="menu__item menu__item--quiet menu__item--centred"
          onClick={onClose}
        >
          <span className="menu__label">Cancel</span>
        </button>
      </div>
    </Modal>
  );
}
